import { CylinderGeometry, Mesh, Group, Vector3 } from "three";
import { COIN_RADIUS } from "./scene/constants.js";

const RESTING_COIN_SPIN = Math.PI * -0.15;
const RESTING_ORBIT_RADIUS = 2.75;
const MOUSE_POSITION_SCALE = 4;
const HOVER_DISTANCE = 2;
const COIN_SIDES = 64;
const COIN_THICKNESS = 0.2;

// Weighted average of two values
const mix = (a, b, weight = 1) => {
  return (a * weight + b) / (weight + 1);
}

class Coin {
  geometry = new CylinderGeometry(COIN_RADIUS, COIN_RADIUS, COIN_THICKNESS, COIN_SIDES);
  materials = [];
  mesh = new Mesh(this.geometry, this.materials);
  container = new Group();
  orbitalRotation = 0; // Rotation about the center of the wheel of coins
  orbitalRotationPerCoin = 0; // The number of radians between each coin in the wheel
  orbitRadius = RESTING_ORBIT_RADIUS; // The radius of the orbit of the coin
  coinSpin = RESTING_COIN_SPIN; // The spin of the coin in radians
  materialsPromise = null; // Promise for async material loading
  currentLayoutScale = 1; // Current layout scale applied to coin

  constructor(scene, index, totalCoins, label, textureManager) {
    this.index = index;
    this.label = label;
    this.totalCoins = totalCoins;
    this.rotationPerCoin = (Math.PI * 2) / this.totalCoins;
    this.orbitalRotation = this.index * this.rotationPerCoin;

    // Setup the mesh
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.container.add(this.mesh);
    scene.add(this.container);
    this.mesh.position.x = this.orbitRadius;
    this.mesh.rotation.x = this.coinSpin;
    this.container.rotation.x = Math.PI * 0.5; // Fix the orientation of the cylinder geometry

    // Determine colorway: even indices = blue, odd indices = orange
    const colorway = this.index % 2 === 0 ? 'blue' : 'orange';

    // Get materials from texture manager (async)
    this.materialsPromise = textureManager.getCoinMaterials(this.label, colorway).then((materials) => {
      this.materials = materials;
      this.mesh.material = materials;
      return materials;
    });
  }

  setCoinSpin(spin) {
    this.coinSpin = RESTING_COIN_SPIN - spin;
    this.mesh.rotation.x = this.coinSpin;
  }

  setOrbitRadius(radius) {
    this.orbitRadius = radius;
    this.mesh.position.x = this.orbitRadius;
  }

  update({
    spinsPerSecond, // number of spins per second
    timeDelta, // time delta in seconds
    mousePos, // position in 3D space normalized to -1 to 1
    state, // current component state
    skipRotationUpdate = false, // if true, don't update container rotation
    skipCoinSpinUpdate = false, // if true, don't update mesh rotation.x (for reveal animation)
    layoutScale = 1, // scale factor used to keep the ring inside the current viewport
    prefersReducedMotion = false, // if true, disable hover effects
    } = {spinsPerSecond: 0, timeDelta: 1000 / 60, state: "ready", skipRotationUpdate: false, skipCoinSpinUpdate: false, layoutScale: 1, prefersReducedMotion: false}) {
    if (this.currentLayoutScale !== layoutScale) {
      this.currentLayoutScale = layoutScale;
      this.mesh.scale.set(layoutScale, layoutScale, layoutScale);
    }

    this.orbitalRotation -= spinsPerSecond * (timeDelta / 1000);
    if (!skipRotationUpdate) {
      this.container.rotation.y = this.orbitalRotation;
    }

    // Disable mouse interaction when not "ready" or "complete"
    if ( state !== "ready" && state !== "complete") {
      if (!skipCoinSpinUpdate) {
        this.setCoinSpin(mix(RESTING_COIN_SPIN, this.coinSpin, 1));
      }
      return;
    }

    // Skip hover effects if user prefers reduced motion
    if (!prefersReducedMotion) {
      const coinPosition = new Vector3();
      coinPosition.setFromMatrixPosition( this.mesh.matrixWorld );

      const scaledMousePositionScale = MOUSE_POSITION_SCALE * layoutScale;
      const scaledHoverDistance = HOVER_DISTANCE * layoutScale;
      const mousePosition = new Vector3(mousePos.x * scaledMousePositionScale, mousePos.y * scaledMousePositionScale , 0);

      const distanceFromMouse = coinPosition.distanceTo(mousePosition);

      if (!skipCoinSpinUpdate) {
        if(distanceFromMouse < scaledHoverDistance) {
          let spinStrength = (scaledHoverDistance - distanceFromMouse) / scaledHoverDistance;
          spinStrength = spinStrength;
          // Rotate coin
          this.setCoinSpin(mix(Math.PI * 3 * spinStrength, this.coinSpin, 1));
        } else {
          this.setCoinSpin(mix(RESTING_COIN_SPIN, this.coinSpin, 1));
        }
      }
    } else if (!skipCoinSpinUpdate) {
      // When reduced motion is preferred, just keep coin at resting spin
      this.setCoinSpin(mix(RESTING_COIN_SPIN, this.coinSpin, 1));
    }

  }

}

export default Coin;
